/**
 * 清溪天成模版 - 列表页
 * 
 * 设计：顶部滚动分类Pill + 左侧齿叠卡片列表 + 右侧悬浮筛选面板
 * 独特排版：列表卡片采用错位重叠设计（z-index递减），配合齿形边缘
 */

import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/templates/crystal/components/Navbar";
import Footer from "@/templates/crystal/components/Footer";
import { getSiteCategories, getListings } from "@/utils/api";

export default function CrystalCategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCatSlug = searchParams.get("cat") || "";
  const currentKeyword = searchParams.get("keyword") || "";

  const [categories, setCategories] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [sortOrder, setSortOrder] = useState("default");

  // 加载分类
  useEffect(() => {
    getSiteCategories().then((data) => {
      setCategories(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  // 加载列表
  const fetchListings = useCallback(async (pageNum: number, reset: boolean) => {
    setLoading(true);
    try {
      const params: any = { page: pageNum, pageSize: 10 };
      if (currentCatSlug) params.categorySlug = currentCatSlug;
      if (currentKeyword) params.keyword = currentKeyword;
      if (sortOrder === "price_desc") params.sort = "price desc";
      else if (sortOrder === "price_asc") params.sort = "price asc";
      else params.sort = "created_at desc";

      const res = await getListings(params);
      const list = res?.list || res?.data?.list || [];
      const t = res?.total || res?.data?.total || 0;
      setTotal(t);
      setHasMore(pageNum * 10 < t);

      if (reset) {
        setListings(list);
      } else {
        setListings((prev) => [...prev, ...list]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentCatSlug, currentKeyword, sortOrder]);

  useEffect(() => {
    setPage(1);
    fetchListings(1, true);
  }, [fetchListings]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchListings(nextPage, false);
  };

  const handleCatClick = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    if (slug) params.set("cat", slug);
    else params.delete("cat");
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar />

      {/* 分类Pill滚动条 */}
      <div className="sticky top-16 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
            <button
              onClick={() => handleCatClick("")}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0 ${!currentCatSlug ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25" : "bg-gray-100 text-gray-600 hover:bg-cyan-50 hover:text-cyan-600"}`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCatClick(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0 ${currentCatSlug === cat.slug ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25" : "bg-gray-100 text-gray-600 hover:bg-cyan-50 hover:text-cyan-600"}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              {currentKeyword ? `搜索结果: "${currentKeyword}"` : currentCatSlug ? categories.find((c) => c.slug === currentCatSlug)?.name || "分类浏览" : "全部分类"}
            </h1>
            <p className="text-xs text-gray-400 mt-1">共 {total} 条信息</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="text-sm bg-white border border-gray-200 rounded-full px-4 py-2 text-gray-600 outline-none focus:border-cyan-400 cursor-pointer"
            >
              <option value="default">默认排序</option>
              <option value="price_desc">价格从高到低</option>
              <option value="price_asc">价格从低到高</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* 左侧：列表 */}
          <div className="flex-1 min-w-0">
            {loading && listings.length === 0 ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse flex gap-4 p-4 bg-white rounded-2xl">
                    <div className="w-28 h-24 bg-gray-100 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-2/3" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-cyan-50">
                  <i className="ri-inbox-line text-2xl text-cyan-400" />
                </div>
                <p className="text-gray-400">暂无相关信息</p>
                <Link to="/publish" className="inline-block mt-4 text-sm text-cyan-600 font-semibold hover:text-cyan-700">
                  去发布第一条信息
                </Link>
              </div>
            ) : (
              <div className="space-y-1">
                {listings.map((item, idx) => (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="flex items-stretch gap-4 p-4 bg-white border border-gray-100 hover:border-cyan-200 hover:shadow-lg hover:shadow-cyan-500/5 rounded-2xl transition-all duration-300 group"
                  >
                    <div className="w-28 h-24 rounded-xl overflow-hidden bg-gradient-to-br from-cyan-100/40 to-blue-50/40 shrink-0">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-2xl text-cyan-200" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-cyan-50 text-cyan-600 text-xs rounded-full font-medium">{item.category_name || "信息"}</span>
                          {item.is_top === 1 && <span className="px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded-full font-medium">置顶</span>}
                        </div>
                        <h3 className="font-semibold text-gray-800 group-hover:text-cyan-600 transition-colors line-clamp-1 text-sm">{item.title}</h3>
                        <p className="text-xs text-gray-400 line-clamp-1 mt-1">{item.description || item.location}</p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1"><i className="ri-map-pin-line text-cyan-400" />{item.city_name || item.district || "未知"}</span>
                        <span>{item.created_at ? new Date(item.created_at).toLocaleDateString("zh-CN") : ""}</span>
                      </div>
                    </div>
                    {item.price && (
                      <div className="flex items-end shrink-0">
                        <span className="text-lg font-bold text-cyan-600">¥{item.price}</span>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}

            {hasMore && (
              <div className="text-center mt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-8 py-2.5 border-2 border-cyan-200 text-cyan-600 rounded-full font-semibold hover:bg-cyan-50 transition-all text-sm disabled:opacity-50"
                >
                  {loading ? "加载中..." : "加载更多"}
                </button>
              </div>
            )}
          </div>

          {/* 右侧：筛选面板 */}
          <div className="lg:w-64 shrink-0">
            <div className="sticky top-32 bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                <i className="ri-equalizer-line text-cyan-500" />
                快速筛选
              </h3>

              <div className="space-y-2">
                {categories.slice(0, 8).map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCatClick(cat.slug)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all ${currentCatSlug === cat.slug ? "bg-cyan-50 text-cyan-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}
                  >
                    <i className={`${cat.icon || "ri-apps-2-line"} mr-2 text-cyan-400`} />
                    {cat.name}
                  </button>
                ))}
              </div>

              {currentCatSlug && (
                <button
                  onClick={() => handleCatClick("")}
                  className="w-full mt-4 py-2 text-xs text-gray-400 hover:text-cyan-600 transition-colors"
                >
                  清除筛选
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
